import axios from 'axios';

const API_URL = 'http://localhost:5002/api';

export interface ReconciliationSetup {
  account: {
    id: string;
    accountName: string;
    accountNumber: string;
    bankName: string;
    balance: number;
  };
  lastReconciliation: {
    date: string;
    endingBalance: number;
  } | null;
}

export interface ReconciliationSession {
  id: string;
  statementDate: string;
  beginningBalance: number;
  endingBalance: number;
  serviceCharge: number;
  serviceChargeDate: string | null;
  serviceChargeAccount: string | null;
  interestEarned: number;
  interestEarnedDate: string | null;
  interestEarnedAccount: string | null;
}

export interface Transaction {
  id: string;
  date: string;
  checkNumber?: string;
  payee?: string;
  memo?: string;
  type?: string;
  amount: number;
  cleared: boolean;
  source?: 'payment' | 'transaction';
}

export interface ReconciliationData {
  reconciliation: ReconciliationSession;
  checksAndPayments: Transaction[];
  depositsAndCredits: Transaction[];
}

export interface ReconciliationSummary {
  clearedBalance: number;
  difference: number;
}

const getAuthToken = () => {
  const token = localStorage.getItem('token');
  return token;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const reconciliationService = {
  // Get reconciliation setup for a bank account
  async getReconciliationSetup(bankAccountId: string): Promise<ReconciliationSetup> {
    const response = await axios.get(
      `${API_URL}/reconciliation/bank-accounts/${bankAccountId}/reconciliation/setup`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Start a new reconciliation
  async startReconciliation(
    bankAccountId: string,
    data: {
      statementDate: string;
      statementEndingBalance: number;
      serviceCharge?: number;
      serviceChargeDate?: string;
      serviceChargeAccount?: string;
      interestEarned?: number;
      interestEarnedDate?: string;
      interestEarnedAccount?: string;
    }
  ): Promise<ReconciliationData> {
    const response = await axios.post(
      `${API_URL}/reconciliation/bank-accounts/${bankAccountId}/reconciliation/start`,
      data,
      getAuthHeaders()
    );
    return response.data;
  },

  // Update cleared transactions
  async updateClearedTransactions(
    reconciliationId: string,
    data: {
      clearedPayments: string[];
      clearedTransactions: string[];
    }
  ): Promise<ReconciliationSummary> {
    const response = await axios.put(
      `${API_URL}/reconciliation/reconciliations/${reconciliationId}/cleared`,
      data,
      getAuthHeaders()
    );
    return response.data;
  },

  // Complete reconciliation
  async completeReconciliation(reconciliationId: string): Promise<{ message: string }> {
    const response = await axios.post(
      `${API_URL}/reconciliation/reconciliations/${reconciliationId}/complete`,
      {},
      getAuthHeaders()
    );
    return response.data;
  },

  // Cancel reconciliation
  async cancelReconciliation(reconciliationId: string): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_URL}/reconciliation/reconciliations/${reconciliationId}`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Get reconciliation history
  async getReconciliationHistory(bankAccountId: string): Promise<any[]> {
    const response = await axios.get(
      `${API_URL}/reconciliation/bank-accounts/${bankAccountId}/reconciliation/history`,
      getAuthHeaders()
    );
    return response.data.reconciliations;
  },
};
